/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import {
  filterCollectionStories,
  getCollectionStories,
  getAuthorStories,
  tagPageStories,
  getDynamicAssets,
  normalizeHomeCollectionStoryHeadlines,
  normalizeStoryItem
} from "../../utils";
import { useSelector } from "react-redux";
import "./with-subsequent-load.m.css";

const getStoryId = (item) => {
  return item?.story?.id || item?.story?.["story-content-id"] || item?.id || null;
};

const WithSubsequentLoad = (Component, defaultLoadCount = 6, hideFirstCard = false, enableInitialLoad = false) => {
  const WrappedComponent = ({ collection = {}, config = {}, index, collectionIndex }) => {
    const mountAtPrefix = useSelector(state => get(state, ["qt", "config", "mountAt"], ""));
    const pageType = useSelector(state => get(state, ["qt", "pageType"], ""));
    const tagSlugs = useSelector(state => get(state, ["qt", "data", "tagSlug"], ""));
    const author = useSelector(state => get(state, ["qt", "data", "author"], {}));
    const memberSubscriptions = useSelector(state => get(state, ["memberSubscriptions"], []));

    const initialLoadCount = parseInt(get(config, ["initialLoadCount"], defaultLoadCount), 10) || defaultLoadCount;
    const subsequentLoadCount = parseInt(get(config, ["subsequentLoadCount"], 8), 10) || 8;

    const collectionStories = filterCollectionStories(collection.items || []).map(normalizeStoryItem);
    // Preserve SSR-rendered collection items
    const [collectionItems, setCollectionItems] = useState(collectionStories);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [hasError, setHasError] = useState(false);

    const [currentOffset, setCurrentOffset] = useState(collectionStories.length);

    // Concurrency and unmount safety refs
    const isFetchingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const hasErrorRef = useRef(false);
    const collectionItemsRef = useRef(collectionItems);
    const offsetRef = useRef(currentOffset);
    const sentinelRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
      hasMoreRef.current = hasMore;
    }, [hasMore]);

    useEffect(() => {
      hasErrorRef.current = hasError;
    }, [hasError]);

    useEffect(() => {
      collectionItemsRef.current = collectionItems;
    }, [collectionItems]);

    useEffect(() => {
      offsetRef.current = currentOffset;
    }, [currentOffset]);

    // When collection prop changes (e.g., initial SSR hydrate), ensure collectionItems are synchronized
    useEffect(() => {
      const freshStories = filterCollectionStories(collection.items || []).map(normalizeStoryItem);
      if (freshStories.length > 0 && collectionItemsRef.current.length === 0) {
        setCollectionItems(freshStories);
        setCurrentOffset(freshStories.length);
      }
    }, [collection.items]);

    const getMoreStories = useCallback(async (requestedOffset, requestedLimit) => {
      if (isFetchingRef.current || !hasMoreRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setHasError(false);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = typeof AbortController !== "undefined" ? new AbortController() : null;

      const offset = typeof requestedOffset === "number" ? requestedOffset : offsetRef.current;
      const limit = typeof requestedLimit === "number" ? requestedLimit : subsequentLoadCount;

      try {
        let response;
        switch (pageType) {
          case "author-page": {
            const authorId = get(author, ["id"], "");
            response = await getAuthorStories(authorId, offset, limit, mountAtPrefix);
            break;
          }
          case "tag-page": {
            const storyFields =
              "slug,metadata,story-template,story-content-id,id,headline,alternative,hero-image-s3-key,hero-image-metadata,sections,tags,author-name,author-id,authors,created-at,first-published-at,published-at,last-published-at,url,read-time";
            const tagStoriesData = await tagPageStories(offset, limit, mountAtPrefix, tagSlugs, storyFields);
            const tagItems = (tagStoriesData?.stories || []).map(item => ({ story: item, type: "story" }));
            response = { items: tagItems, "total-count": tagStoriesData?.total };
            break;
          }
          case "purchased-details": {
            const dynamicItems = await getDynamicAssets(memberSubscriptions, offset, limit, "SubsequentLoad");
            response = { items: dynamicItems || [] };
            break;
          }
          default: {
            response = await getCollectionStories(collection.slug, offset, limit, mountAtPrefix);
            break;
          }
        }

        const rawItems = response?.items || [];
        if (!response || !Array.isArray(rawItems)) {
          setHasError(true);
          setIsLoading(false);
          isFetchingRef.current = false;
          return;
        }

        if (rawItems.length === 0) {
          setHasMore(false);
          setIsLoading(false);
          isFetchingRef.current = false;
          return;
        }

        // Deduplicate against already-rendered stories
        const seenIds = new Set(
          collectionItemsRef.current
            .map(getStoryId)
            .filter(Boolean)
        );

        const newItems = rawItems
          .filter(item => {
            const id = getStoryId(item);
            return !id || !seenIds.has(id);
          })
          .map(normalizeStoryItem);

        if (newItems.length > 0) {
          const itemsToAppend = pageType === "home-page"
            ? normalizeHomeCollectionStoryHeadlines({ items: newItems }).items
            : newItems;
          setCollectionItems(prev => [...prev, ...itemsToAppend]);
        }

        const nextOffset = offset + rawItems.length;
        setCurrentOffset(nextOffset);

        const totalCount = response["total-count"];
        if (
          rawItems.length < limit ||
          (typeof totalCount === "number" && nextOffset >= totalCount)
        ) {
          setHasMore(false);
        }
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("Failed to load more stories:", err);
          setHasError(true);
        }
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }, [author, collection.slug, memberSubscriptions, mountAtPrefix, pageType, subsequentLoadCount, tagSlugs]);

    // Setup IntersectionObserver for infinite scrolling
    useEffect(() => {
      if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
        return;
      }

      const sentinelElement = sentinelRef.current;
      if (!sentinelElement) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry || !entry.isIntersecting) {
            return;
          }

          // Check if sentinel is within a visible container (not hidden by inactive tab)
          if (sentinelElement.offsetParent === null) {
            return;
          }

          if (
            !isFetchingRef.current &&
            hasMoreRef.current &&
            !hasErrorRef.current
          ) {
            getMoreStories();
          }
        },
        {
          root: null,
          rootMargin: "0px 0px 400px 0px",
          threshold: 0.01,
        }
      );

      observer.observe(sentinelElement);

      return () => {
        observer.disconnect();
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, [getMoreStories]);

    const handleRetry = useCallback(() => {
      setHasError(false);
      getMoreStories();
    }, [getMoreStories]);

    const updatedCollection = { ...collection, items: collectionItems };
    const cleanConfig = {
      ...config,
      footerButton: "",
      showButton: false,
    };

    return (
      <React.Fragment>
        <Component
          collection={updatedCollection}
          config={cleanConfig}
          getMoreStories={getMoreStories}
          isLoadMoreVisible={false}
          isLoading={isLoading}
          index={index}
          isolatedLoadMore={false}
          hideFirstCard={hideFirstCard}
          collectionIndex={collectionIndex}
        />
        {hasMore && !hasError && (
          <div
            ref={sentinelRef}
            styleName="infinite-scroll-sentinel"
            data-test-id="infinite-scroll-sentinel"
            aria-hidden="true"
          />
        )}
        {isLoading && (
          <div
            styleName="infinite-scroll-status"
            data-test-id="infinite-scroll-loading"
            role="status"
            aria-live="polite"
          >
            <div styleName="infinite-scroll-spinner" aria-label="Loading more stories" />
          </div>
        )}
        {hasError && (
          <div
            styleName="infinite-scroll-error"
            data-test-id="infinite-scroll-error"
            role="alert"
          >
            <span>Unable to load more stories.</span>
            <button
              type="button"
              styleName="infinite-scroll-retry-button"
              data-test-id="infinite-scroll-retry-button"
              onClick={handleRetry}
            >
              Retry
            </button>
          </div>
        )}
      </React.Fragment>
    );
  };

  WrappedComponent.propTypes = {
    collection: PropTypes.object.isRequired,
    config: PropTypes.object,
    index: PropTypes.number,
    collectionIndex: PropTypes.number
  };

  return WrappedComponent;
};

export default WithSubsequentLoad;
